import { $, $$ } from '@utils';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';

function init() {
  if (!$('[data-au-input-range]')) return;

  addEventListeners();
}

function addEventListeners() {
  $$('[data-au-input-range]').forEach((container) => {
    const rangeInput = container.querySelector('input[type="range"]');
    const numberInput = container.querySelector('input[type="number"]');
    const helpText = container.querySelector('[data-au-input-range-help-text]');
    const maxOutput = parseFloat(rangeInput.getAttribute('max'));
    const minOutput = parseFloat(rangeInput.getAttribute('min'));
    const step = parseFloat(rangeInput.getAttribute('step')) || 1;

    setupSlider(minOutput, maxOutput, step, rangeInput, numberInput, helpText);
  });
}

function getDynamicSteps(minOutput, maxOutput, step) {
  return Math.min(maxOutput / step, 50); // Adjust dynamic steps according to the step
}

function getExponentialValues(minOutput, maxOutput, step) {
  const steps = getDynamicSteps(minOutput, maxOutput, step);
  const values = [];
  const factor = Math.exp(Math.log(maxOutput / Math.max(minOutput, 1)) / (steps - 1)); // Avoid division by 0

  for (let i = 0; i < steps; i++) {
    let value = parseFloat(Math.max(minOutput, 1 * Math.pow(factor, i)).toFixed(2)); // Use Math.max to avoid NaN
    if (values.length > 0 && value <= values[values.length - 1]) {
      value = parseFloat((values[values.length - 1] + step).toFixed(2));
    }

    values.push(value);
  }

  values[0] = minOutput; // Ensure the first value is exactly minOutput
  values[values.length - 1] = maxOutput; // Ensure the last value is exactly maxOutput
  return values;
}

function setupSlider(minOutput, maxOutput, step, rangeInput, numberInput, helpText) {
  const values = getExponentialValues(minOutput, maxOutput, step);

  // Slider bounds (position)
  const maxRangeValue = parseFloat(rangeInput.getAttribute('max'));
  const minRangeValue = parseFloat(rangeInput.getAttribute('min')) || 0;

  // Utils
  const clamp = (x, lo, hi) => Math.min(Math.max(x, lo), hi);
  const normalize = (s) => {
    const raw = String(s ?? '').trim();
    if (!raw) return NaN;
    return parseFloat(raw.replace(',', '.')); // accept comma or dot
  };

  // Decimals to display: use the greater of the number input’s step and the range input’s step
  const rangeStepAttr = rangeInput.getAttribute('step');
  const numberStepAttr = numberInput ? numberInput.getAttribute('step') : null;

  const countDecimalsFromStep = (attr) => {
    if (!attr || attr === 'any') return null;
    const dot = attr.indexOf('.');
    return dot >= 0 ? attr.length - dot - 1 : 0;
  };

  const decimalsRange = countDecimalsFromStep(rangeStepAttr);
  const decimalsNumber = countDecimalsFromStep(numberStepAttr);
  const decimals =
    decimalsRange === null && decimalsNumber === null ? null : Math.max(decimalsRange || 0, decimalsNumber || 0);

  const useFixed = decimals !== null && decimals > 0;

  // Format: do not show decimals if integer; if rounding, do not pad trailing zeros
  const fmt = (x) => {
    const n = Number(x);
    if (Number.isNaN(n)) return '';

    const isInteger = Math.abs(n - Math.round(n)) < 1e-9; // check integer
    if (isInteger) return String(Math.round(n));

    if (useFixed) {
      const s = n.toFixed(decimals); // limit precision by step
      return s.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
    }

    if (decimals === 0) return String(Math.round(n)); // effective step is integer
    return String(n); // step="any" -> no forced rounding
  };

  // Track whether the latest change came from user typing (not from the range)
  let typedByUser = false;

  // Keep user precision: trim trailing zeros, keep the user’s separator
  const tidyUser = (raw) =>
    String(raw)
      .replace(/([.,]\d*?[1-9])0+$/, '$1') // 2,90 -> 2,9
      .replace(/[.,]0+$/, '') // 3,00 -> 3
      .replace(/[.,]$/, ''); // "2," / "2." -> "2"

  // Mappings: position ↔ index ↔ real value
  const posToIndex = (pos) => {
    const t = (pos - minRangeValue) / (maxRangeValue - minRangeValue);
    return Math.round(clamp(t, 0, 1) * (values.length - 1));
  };
  const indexToPos = (idx) => (idx / (values.length - 1)) * (maxRangeValue - minRangeValue) + minRangeValue;

  const posToValue = (pos) => values[posToIndex(pos)];
  const valueToPos = (val) => {
    const i = values.findIndex((v) => v >= val); // first ≥ -> monotonic mapping
    const idx = i === -1 ? values.length - 1 : i;
    return indexToPos(idx);
  };

  // Sync from range → number (format using fmt)
  function updateNumberFromRange() {
    if (!numberInput) return;
    const pos = parseFloat(rangeInput.value);
    const real = posToValue(pos);
    typedByUser = false;
    numberInput.value = fmt(real);
  }

  // Sync from number → range (no formatting here)
  function updateRangeFromNumber() {
    const v = normalize(numberInput.value);
    if (Number.isNaN(v)) return; // intermediate states while typing (e.g., "1," or "1.")
    rangeInput.value = valueToPos(clamp(v, minOutput, maxOutput));
  }

  // On blur: clamp and format; if user typed and it’s in range, preserve their precision
  function limitValues() {
    const raw = String(numberInput.value).trim();
    let v = normalize(raw);
    if (Number.isNaN(v)) v = minOutput;

    const outOfRange = v > maxOutput || v < minOutput || raw === '';
    if (outOfRange) {
      v = clamp(v, minOutput, maxOutput); // out of range -> clamp and show helpText
      numberInput.value = fmt(v);
      if (helpText) {
        helpText.classList.remove('au-hide');
        helpText.setAttribute('aria-hidden', false);
        setTimeout(() => {
          helpText.classList.remove('au-fade-out');
          helpText.classList.add('au-hide');
          helpText.setAttribute('aria-hidden', true);
        }, 2000);
      }
    } else {
      numberInput.value = typedByUser ? tidyUser(raw) : fmt(v);
    }

    rangeInput.value = valueToPos(v);
  }

  // Events
  rangeInput.addEventListener('click', (e) => {
    const rangeLabel = removeAccentMarkAndUppercase(
      e.currentTarget.closest('[data-au-input-range]').querySelector('label').innerText,
    );
    const rangeValue = e.currentTarget.value;

    window.dataLayer.push({
      event: 'navigation',
      content_type: 'clickModulo',
      item_id: `${rangeLabel}_${rangeValue}`,
    });
  });

  rangeInput.addEventListener('input', () => {
    updateNumberFromRange();
    updateSliderGradientAndTooltip(rangeInput, values);
  });

  if (numberInput) {
    numberInput.addEventListener('input', () => {
      typedByUser = true;
      updateRangeFromNumber();
      updateSliderGradientAndTooltip(rangeInput, values);
    });

    numberInput.addEventListener('blur', (e) => {
      limitValues();
      updateSliderGradientAndTooltip(rangeInput, values);

      const rangeLabel = removeAccentMarkAndUppercase(
        e.currentTarget.closest('[data-au-input-range]').querySelector('label').innerText,
      );
      const rangeValue = e.currentTarget.value;

      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickModulo',
        item_id: `${rangeLabel}_${rangeValue}`,
      });
    });

    // Normalize after any external formatter: keep user precision when typed
    numberInput.addEventListener('change', () => {
      setTimeout(() => {
        const raw = String(numberInput.value).trim();
        if (!raw) return;
        if (typedByUser) {
          numberInput.value = tidyUser(raw);
        } else {
          const v = normalize(raw);
          if (!Number.isNaN(v)) numberInput.value = fmt(v);
        }
      }, 0);
    });
  }

  // Initial state
  if (numberInput && numberInput.value !== '') {
    updateRangeFromNumber();
  } else {
    updateNumberFromRange();
  }
  updateSliderGradientAndTooltip(rangeInput, values);
}

function updateSliderGradientAndTooltip(el, values) {
  const maxRangeValue = parseFloat(el.getAttribute('max'));
  const step = parseFloat(el.getAttribute('step')) || 1;
  const minRangeValue = parseFloat(el.getAttribute('min')) || 0;
  const percentage = ((el.value - minRangeValue) / (maxRangeValue - minRangeValue)) * 100;
  const rangeTooltip = el.closest('[data-au-input-range]').querySelector('[data-au-input-range-tooltip]');

  const pxFromLeft = `${(percentage * el.offsetWidth) / 100 - (percentage * rangeTooltip.offsetWidth) / 125}px`;
  const isStepDecimal = step % 1 !== 0; // Check if the step has decimals
  const index = Math.round((el.value / maxRangeValue) * (values.length - 1));
  let tooltipValue = isStepDecimal ? values[index].toFixed(2) : Math.round(values[index]);
  tooltipValue = tooltipValue.toString().endsWith('.00') ? parseInt(tooltipValue, 10) : tooltipValue; // Convert to string before using endsWith

  // set tooltip value
  rangeTooltip.textContent = tooltipValue;
  // set gradient width
  el.style.setProperty('--gradient-width', `${100 - percentage}%`);
  // set tooltip position
  rangeTooltip.style.setProperty('left', pxFromLeft);
}

export default { init };
